import * as React from 'react';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	TextField,
	Button,
	FormControlLabel,
	Checkbox,
	Box,
	MenuItem,
	Select,
	InputLabel,
	FormControl,
	Typography,
} from '@mui/material';
import slugify from 'slugify';
import {
	EntityType,
	FormData,
	Repository,
	PropertyDefinition,
	PropertyDefinitionBelongsTo,
} from '../types';
import EntityTypeSelectButton from './EntityTypeSelectButton';
import { PROPERTY_DEFINITION_TYPES, REPOSITORIES_BY_ENTITY_TYPE } from '../constants';
import { getRepositorySingleUnitLabel } from '../common/util';
import { useAppSelector } from '../hooks';
import { selectAllRepositories, selectAllPropertyDefinitions, selectLocationId } from '../containers/contentSlice';
import { selectIsAuthenticated } from '../containers/authSlice';
import { useGetRepositoryByIdQuery } from '../services/repositoryEntries';
import { DataTypeAvatar } from './DataTypeAvatar';
import ReactGA from 'react-ga4';

interface DialogProps {
	open: boolean;
	onClose: () => void;
	onSubmit: (formData: FormData) => void;
}

interface RepositoryPropertyFormValues {
	label: string;
	slug: string;
	required: boolean;
	targetRepositoryId: string;
	allowMany: boolean;
	targetPropertySlug: string;
}

const getInitialValues = (): RepositoryPropertyFormValues => ({
	label: '',
	slug: '',
	required: true,
	targetRepositoryId: '',
	allowMany: false,
	targetPropertySlug: '',
});

const AddRepositoryPropertyTextOrBooleanForm: React.FC<{
	values: RepositoryPropertyFormValues;
	onChange: (patch: Partial<RepositoryPropertyFormValues>) => void;
	hideRequired?: boolean;
}> = ({values, onChange, hideRequired}) => {
	const handleLabelChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const nextLabel = e.target.value;
		const nextSlug = slugify(nextLabel, {lower: true});
		onChange({
			label: nextLabel,
			// Always mirror label -> slug for this specialized form.
			slug: nextSlug,
		});
	};

	return (
		<Box display="flex" flexDirection="column" gap={2} mt={1}>
			<TextField
				label="Label"
				value={values.label}
				onChange={handleLabelChange}
				variant="standard"
				fullWidth
				autoFocus
				required
			/>
			<TextField
				label="Key"
				value={values.slug}
				// onChange={(e) => onChange({slug: e.target.value})}
				fullWidth
				variant="standard"
				required
				disabled
			/>
			{!hideRequired && (
				<FormControlLabel
					control={
						<Checkbox
							checked={values.required}
							onChange={(e) => onChange({required: e.target.checked})}
						/>
					}
					label="Required"
				/>
			)}
		</Box>
	);
};

const AddRepositoryPropertyBelongsToForm: React.FC<{
	values: RepositoryPropertyFormValues;
	onChange: (patch: Partial<RepositoryPropertyFormValues>) => void;
	repositories: Repository[];
}> = ({values, onChange, repositories}) => {
	const handleLabelChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const nextLabel = e.target.value;
		const nextSlug = slugify(nextLabel, {lower: true});
		onChange({
			label: nextLabel,
			slug: nextSlug,
		});
	};

	return (
		<Box display="flex" flexDirection="column" gap={2} mt={1}>
			<TextField
				label="Label"
				value={values.label}
				onChange={handleLabelChange}
				variant="standard"
				fullWidth
				autoFocus
				required
			/>
			<TextField
				label="Key"
				value={values.slug}
				onChange={(e) => onChange({slug: e.target.value})}
				fullWidth
				variant="standard"
				required
				disabled
			/>
			<FormControl fullWidth variant="standard" required>
				<InputLabel id="belongs-to-target-repository-label">Other collection</InputLabel>
				<Select
					labelId="belongs-to-target-repository-label"
					value={values.targetRepositoryId}
					onChange={(e) => onChange({targetRepositoryId: e.target.value as string})}
				>
					{!repositories.length && <MenuItem value="">No other collections found</MenuItem>}
					{repositories.map((repository) => (
						<MenuItem key={repository.id} value={repository.id}>
							{repository.title}
						</MenuItem>
					))}
				</Select>
			</FormControl>
			<FormControlLabel
				control={
					<Checkbox
						checked={values.required}
						onChange={(e) => onChange({required: e.target.checked})}
					/>
				}
				label="Required"
			/>
			<FormControlLabel
				control={
					<Checkbox
						checked={values.allowMany}
						onChange={(e) => onChange({allowMany: e.target.checked})}
					/>
				}
				label="Allow many"
			/>
		</Box>
	);
};

const AddRepositoryPropertyHasForm: React.FC<{
	values: RepositoryPropertyFormValues;
	onChange: (patch: Partial<RepositoryPropertyFormValues>) => void;
	repositories: Repository[];
	targetRepositoryProperties: PropertyDefinition[];
	isLoadingTargetProperties?: boolean;
}> = ({values, onChange, repositories, targetRepositoryProperties, isLoadingTargetProperties}) => {
	const handleLabelChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const nextLabel = e.target.value;
		const nextSlug = slugify(nextLabel, {lower: true});
		onChange({
			label: nextLabel,
			slug: nextSlug,
		});
	};

	return (
		<Box display="flex" flexDirection="column" gap={2} mt={1}>
			<TextField
				label="Label"
				value={values.label}
				onChange={handleLabelChange}
				fullWidth
				variant="standard"
				autoFocus
				required
			/>
			<TextField
				label="Key"
				value={values.slug}
				onChange={(e) => onChange({slug: e.target.value})}
				fullWidth
				variant="standard"
				required
				disabled
			/>
			<FormControl fullWidth variant="standard" required>
				<InputLabel id="has-target-repository-label">Referenced in...</InputLabel>
				<Select
					labelId="has-target-repository-label"
					value={values.targetRepositoryId}
					onChange={(e) => {
						onChange({
							targetRepositoryId: e.target.value as string,
							targetPropertySlug: '',
						});
					}}
				>
					{!repositories.length && <MenuItem value="">No other collections found</MenuItem>}
					{repositories.map((repository) => (
						<MenuItem key={repository.id} value={repository.id}>
							{repository.title}
						</MenuItem>
					))}
				</Select>
			</FormControl>
			<FormControl
				fullWidth
				variant="standard"
				required
				disabled={!values.targetRepositoryId || isLoadingTargetProperties}
			>
				<InputLabel id="has-target-property-label">As...</InputLabel>
				<Select
					labelId="has-target-property-label"
					value={values.targetPropertySlug}
					onChange={(e) => onChange({targetPropertySlug: e.target.value as string})}
				>
					{isLoadingTargetProperties && (
						<MenuItem value="" disabled>
							Loading properties…
						</MenuItem>
					)}
					{!isLoadingTargetProperties &&
						targetRepositoryProperties.map((prop) => (
							<MenuItem key={prop.id} value={prop.slug}>
								{prop.label}
							</MenuItem>
						))}
				</Select>
			</FormControl>
			<FormControlLabel
				control={
					<Checkbox
						checked={values.required}
						onChange={(e) => onChange({required: e.target.checked})}
					/>
				}
				label="Required"
			/>
		</Box>
	);
};

export const AddRepositoryPropertyDialog: React.FC<DialogProps> = ({
	open,
	onClose,
	onSubmit,
}) => {
	const allRepositories = useAppSelector(selectAllRepositories);
	const allPropertyDefinitions = useAppSelector(selectAllPropertyDefinitions);
	const isAuthenticated = useAppSelector(selectIsAuthenticated);
	const [type, setType] = useState<EntityType>(EntityType.PropertyDefinitionString);
	const [values, setValues] = useState<RepositoryPropertyFormValues>(getInitialValues);
	const locationId = useAppSelector(selectLocationId);
	const propertyDefinitionConfig = REPOSITORIES_BY_ENTITY_TYPE[type as EntityType];

	const { data: targetRepositoryByIdForHas, isFetching: isTargetRepositoryForHasFetching } =
		useGetRepositoryByIdQuery(values.targetRepositoryId, {
			skip:
				!open ||
				!isAuthenticated ||
				!values.targetRepositoryId ||
				type !== EntityType.PropertyDefinitionHas,
		});

	const handleChange = useCallback((patch: Partial<RepositoryPropertyFormValues>) => {
		setValues((prev) => ({
			...prev,
			...patch,
		}));
	}, []);

	const handleTypeChange = (type: EntityType) => {
		setType(type);
	};

	// Reset values whenever the dialog is opened or the property type changes.
	useEffect(() => {
		if (open) {
			setValues(getInitialValues());
		}
	}, [open, type]);

	const handleDialogClose = () => {
		setValues(getInitialValues());
		onClose();
	};

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();

		ReactGA.event({
			category: 'UI',
			action: 'AddRepositoryPropertySubmitted',
		});

		let payload: FormData;

		switch (type) {
		case EntityType.PropertyDefinitionBelongsTo:
			payload = {
				type: type,
				label: values.label,
				slug: values.slug,
				targetRepositoryId: values.targetRepositoryId,
				required: values.required,
				allowMany: values.allowMany,
			};
			break;
		case EntityType.PropertyDefinitionHas:
			payload = {
				type: type,
				label: values.label,
				slug: values.slug,
				targetRepositoryId: values.targetRepositoryId,
				targetPropertySlug: values.targetPropertySlug,
				required: values.required,
			};
			break;
		// Treat string/text/boolean the same here: simple scalar property.
		default:
			payload = {
				type: type,
				label: values.label,
				slug: values.slug,
				required: values.required,
			};
			break;
		}

		onSubmit(payload);
	};

	const targetRepositoryPropertiesForHas = useMemo(() => {
		const belongsToPointingHere = (defs: PropertyDefinition[]) =>
			defs.filter(
				(def) =>
					def.type === EntityType.PropertyDefinitionBelongsTo &&
					(def as PropertyDefinitionBelongsTo).targetRepositoryId === locationId,
			);
		if (isAuthenticated && targetRepositoryByIdForHas?.propertyDefinitions) {
			return belongsToPointingHere(targetRepositoryByIdForHas.propertyDefinitions);
		}
		return belongsToPointingHere(
			allPropertyDefinitions.filter((def) => def.parentId === values.targetRepositoryId),
		);
	}, [
		isAuthenticated,
		targetRepositoryByIdForHas,
		allPropertyDefinitions,
		values.targetRepositoryId,
		locationId,
	]);

	const formContent = useMemo(() => {
		switch (type) {
		case EntityType.PropertyDefinitionBelongsTo:
			return (
				<AddRepositoryPropertyBelongsToForm
					values={values}
					onChange={handleChange}
					repositories={allRepositories.filter((repository) => repository.id !== locationId)}
				/>
			);
		case EntityType.PropertyDefinitionHas:
			return (
				<AddRepositoryPropertyHasForm
					values={values}
					onChange={handleChange}
					repositories={allRepositories.filter((repository) => repository.id !== locationId)}
					targetRepositoryProperties={targetRepositoryPropertiesForHas}
					isLoadingTargetProperties={
						isAuthenticated && !!values.targetRepositoryId && isTargetRepositoryForHasFetching
					}
				/>
			);
		case EntityType.PropertyDefinitionBoolean: {
			return (
				<AddRepositoryPropertyTextOrBooleanForm
					values={values}
					onChange={handleChange}
					hideRequired={true}
				/>
			);
		}
		default:
			// Fallback for both text and long text property definitions.
			return (
				<AddRepositoryPropertyTextOrBooleanForm
					values={values}
					onChange={handleChange}
				/>
			);
		}
	}, [
		type,
		values,
		handleChange,
		allRepositories,
		locationId,
		targetRepositoryPropertiesForHas,
		isAuthenticated,
		isTargetRepositoryForHasFetching,
	]);

	if (!type) {
		return null;
	}

	return (
		<Dialog
			open={open}
			onClose={handleDialogClose}
			maxWidth="xs"
			fullWidth
		>
			<Box display="flex" justifyContent="space-between" alignItems="center">
				<DialogTitle sx={{display: 'flex', alignItems: 'center', gap: 1.5}}><DataTypeAvatar type={type} />{`New ${getRepositorySingleUnitLabel(REPOSITORIES_BY_ENTITY_TYPE[type as EntityType])}`}</DialogTitle>
				<Box pr={2}>
					<EntityTypeSelectButton
						selectOptions={PROPERTY_DEFINITION_TYPES.map((type) => ({id: type, value: type, label: getRepositorySingleUnitLabel(REPOSITORIES_BY_ENTITY_TYPE[type as EntityType])})) as any}
						onChange={handleTypeChange}
					/>
				</Box>
			</Box>
			{propertyDefinitionConfig.summary && <DialogContent sx={{py:0}}>
				<Typography variant="body2" color="text.secondary">{propertyDefinitionConfig.summary}</Typography>
			</DialogContent>}
			<form onSubmit={handleSubmit} autoComplete="off">
				<DialogContent>
					{formContent}
				</DialogContent>
				<DialogActions>
					<Button onClick={handleDialogClose}>Cancel</Button>
					<Button type="submit" variant="contained">
						Create
					</Button>
				</DialogActions>
			</form>
		</Dialog>
	);
};

export default AddRepositoryPropertyDialog;
